import { ChatIcon } from '@assets/icons';
import { useAuthContext } from '@authentication/Authentication';
import { Button } from '@components/button';
import { agentPageContextAtom, agentPanelOpenAtom } from '@hooks/agentPanelAtoms';
import { useTheme } from '@mui/material';
import { useAtom, useAtomValue } from 'jotai';
import posthog from 'posthog-js';

/**
 * Header button that toggles the agent chat panel open/closed.
 * Only renders when the user is authenticated.
 * Styling replicates the button portion of HeaderMenu for visual consistency.
 */
export const AgentChatToggle = () => {
  const { token } = useAuthContext();
  const { palette } = useTheme();
  const [panelOpen, setPanelOpen] = useAtom(agentPanelOpenAtom);
  const pageContext = useAtomValue(agentPageContextAtom);

  // Don't show the toggle if not authenticated
  if (!token) return null;

  return (
    <Button
      aria-label="Toggle agent chat"
      customContent
      sx={{
        color: 'common.white',
        px: 3,
        backgroundColor: panelOpen ? palette.grey[800] : 'transparent',
        border: 'none',
        display: 'flex',
        justifyContent: 'center',
        '& svg': {
          stroke: panelOpen ? palette.primary.main : palette.grey[500],
          fill: panelOpen ? palette.primary.main : 'transparent',
        },
        '&:hover': {
          backgroundColor: panelOpen ? palette.grey[800] : 'transparent',
          cursor: 'pointer',
          '& svg': { stroke: palette.grey[200] },
        },
      }}
      onClick={() => {
        if (!panelOpen) {
          posthog.capture('agent-chat:user_panel_opened', {
            organizationId: pageContext.organizationId,
            workspaceId: pageContext.workspaceId,
            path: pageContext.path,
          });
        }
        setPanelOpen(!panelOpen);
      }}
      data-testid="agent-chat-toggle"
    >
      <ChatIcon size={20} />
    </Button>
  );
};
